/**
 * Whether this session has a télé-expertise channel at all, and how much is waiting on it.
 *
 * The module is not a role and not a speciality: it exists for the accounts whose clinic actually
 * wired the web form into the API, and for nobody else. A menu entry leading to a permanently empty
 * screen is worse than no entry — it reads as a channel that stopped working.
 *
 * The answer is the backend's (`GET /teleexpertise/disponibilite`), never inferred here from a role
 * or an email: the perimeter is a clinic membership, which only the server holds.
 */

import { useEffect, useState } from "react";
import API from "@app/services/api";
import useUser from "@app/zustand/user";
import { useViewAsKey } from "@app/services/view-as";

export interface TeleexpertiseAvailability {
  /** True when the reader's clinic has at least one web-form connection. */
  disponible: boolean;
  /** Dossiers nobody has settled yet — what the sidebar badge counts. */
  aTraiter: number;
  total: number;
}

const ABSENT: TeleexpertiseAvailability = {
  disponible: false,
  aTraiter: 0,
  total: 0,
};

export function useTeleexpertiseAvailability(): TeleexpertiseAvailability {
  const user = useUser((state) => state.user);
  // Whose platform is on screen. The eye and the speciality picker both change the answer, so both
  // have to re-ask it — a badge left over from the previous identity counts someone else's queue.
  const viewAsKey = useViewAsKey();
  const [availability, setAvailability] = useState<TeleexpertiseAvailability>(ABSENT);

  useEffect(() => {
    if (!user) {
      setAvailability(ABSENT);
      return;
    }
    let cancelled = false;
    void (async () => {
      const response = (await API.get({ path: "/teleexpertise/disponibilite" })) as {
        ok?: boolean;
        data?: { disponible?: boolean; a_traiter?: number; total?: number };
      };
      if (cancelled) return;
      // Anything other than a clear yes is treated as « no module »: a failed request must hide the
      // entry rather than offer one that cannot answer.
      if (!response?.ok || !response.data?.disponible) {
        setAvailability(ABSENT);
        return;
      }
      setAvailability({
        disponible: true,
        aTraiter: response.data.a_traiter ?? 0,
        total: response.data.total ?? 0,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [user, viewAsKey]);

  return availability;
}
